import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocalizedNavigation } from '../hooks/useLocalizedNavigation';
import { Star, Phone, Mail, Award, Home, MessageCircle } from 'lucide-react';
import { Agent } from '../types';
import { Button } from './ui/button';

interface AgentCardProps {
  agent: Agent;
  showContactButtons?: boolean;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent, showContactButtons = true }) => {
  const { t } = useTranslation();
  const { getTextInCurrentLanguage } = useLanguage();
  const { getLocalizedPath } = useLocalizedNavigation();

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
      <div className="flex items-start gap-4 mb-4">
        {/* Agent Photo */}
        <img
          src={agent.photo}
          alt={getTextInCurrentLanguage(agent.name)}
          className="w-20 h-20 rounded-full object-cover border-2 border-blue-100"
        />

        {/* Agent Info */}
        <div className="flex-1">
          <Link to={getLocalizedPath(`/agent/${agent.id}`)} className="hover:text-blue-600 transition-colors">
            <h3 className="text-xl font-bold text-gray-900 mb-1">{getTextInCurrentLanguage(agent.name)}</h3>
          </Link>

          {/* Rating */}
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="font-semibold text-gray-900">{agent.rating}</span>
            </div>
            <span className="text-sm text-gray-600">
              ({agent.reviewCount} {t('agent.reviews')})
            </span>
          </div>

          {/* Experience */}
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <Award className="h-4 w-4" />
              <span>{agent.yearsExperience} {t('agent.yearsExp')}</span>
            </div>
            <div className="flex items-center gap-1">
              <Home className="h-4 w-4" />
              <span>{agent.propertiesSold} {t('agent.sold')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bio */}
      <p className="text-sm text-gray-700 mb-4 line-clamp-3">{getTextInCurrentLanguage(agent.bio)}</p>

      {/* Specialties */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-2">
          {agent.specialties.slice(0, 3).map((specialty, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
            >
              {getTextInCurrentLanguage(specialty)}
            </span>
          ))}
        </div>
      </div>

      {/* Languages */}
      {agent.languages.length > 0 && (
        <div className="text-xs text-gray-600 mb-4">
          <span className="font-medium">{t('agent.languagesLabel')}</span> {agent.languages.join(', ')}
        </div>
      )}

      {/* Contact Buttons */}
      {showContactButtons && (
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => window.location.href = `tel:${agent.phone}`}
            variant="outline"
            size="sm"
            className="flex-1"
          >
            <Phone className="h-4 w-4 mr-2" />
            {t('agent.call')}
          </Button>
          <Button
            onClick={() => window.location.href = `mailto:${agent.email || 'info@realtypy.com'}`}
            variant="outline"
            size="sm"
            className="flex-1"
          >
            <Mail className="h-4 w-4 mr-2" />
            {t('agent.email')}
          </Button>
          <Button
            onClick={() => {
              const cleanPhone = agent.phone.replace(/\D/g, '');
              window.location.href = `https://wa.me/${cleanPhone}?text=Hola,%20como%20podemos%20ayudarte`;
            }}
            variant="outline"
            size="sm"
            className="flex-1"
          >
            <MessageCircle className="h-4 w-4 mr-2 text-green-500" />
            WhatsApp
          </Button>
        </div>
      )}

      {/* View Profile Link */}
      <Link to={getLocalizedPath(`/agent/${agent.id}`)}>
        <Button variant="ghost" size="sm" className="w-full mt-2 text-blue-600 hover:text-blue-700">
          {t('agent.viewFullProfile')}
        </Button>
      </Link>
    </div>
  );
};

export default AgentCard;
